import styled from "@emotion/styled"
import { ButtonBase } from "@mui/material"
import { useTranslations } from "next-intl"

interface RegionButtonProps {
    $selected?: boolean
}

const RegionButton = styled.label<RegionButtonProps>`
    display: flex;
    align-items: center;
    border-radius: 20px;
    border: 1px solid #5c6474;
    background: ${({ $selected }) => $selected ? '#0050C9' : '#fff'};
    color: ${({ $selected }) => $selected ? '#fff' : '#5C6474'};
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    padding: 5px 10px;
    cursor: pointer;
    transition: background .15s, color .15s, border-color .15s;
    outline: ${({ $selected }) => $selected ? '2px solid #153E69' : 'none'};
    &:hover,
    &:active,
    &:focus-within {
        background: #5C6474;
        color: #fff;
        outline: 2px solid #153E69;
        outline-offset: 2px;
    }
    input {
        position: absolute;
        opacity: 0;
        width: 1px;
        height: 1px;
    }
    input:focus + * {
        outline: 2px solid #153E69;
        outline-offset: 2px;
    }
`

const StyledRegionsFilter = styled.fieldset`
    margin: 0;
    margin-bottom: 24px;
    ${(props) => props.theme.breakpoints.up("md")} {
        display: flex;
        align-items: center;
        gap: 16px;
        width: 100%;
        background-color: #ECF0F3;
        margin-bottom: 0;
        height:70px;
        padding:14px 24px;
        border-bottom:solid 1px #ABBBCA;
    }
    border: 0;
    padding: 0;
`

const StyledRegionsFilterLegend = styled.legend`
    margin-bottom: 24px;
    font-size: 22px;
    font-weight: 500;
    display: contents;
    ${(props) => props.theme.breakpoints.up("md")} {
        margin-bottom: 0;
        font-size: 28px;
        margin-top: 0;
    }
`

const StyledRegionsFilterList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    list-style: none;
    margin: 0;
    padding: 0;
    ${(props) => props.theme.breakpoints.up("md")} {
        flex: 1;
    }
`

const StyledRegionsResetBtn = styled(ButtonBase)`
    margin-inline-start: 0;
    margin-top: 12px;
    font-size: 15px;
    color: #0050C9;
    opacity: ${({ disabled }) => disabled ? 0.35 : 1};
    cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
    padding: 4px 12px;
    border-radius: 16px;
    background: none;
    border: none;
    font-weight: 600;
    align-self: flex-start;
    ${(props) => props.theme.breakpoints.up("md")} {
        margin-inline-start: auto;
        margin-top: 0;
        align-self: center;
    }
    ${(props) => props.theme.breakpoints.down("sm")} {
        display: none;
    }
    &:hover,
    &:active,&:focus-visible {
        text-decoration: underline;
        outline: 2px solid white;
    }
`

interface RegionsFilterProps {
    regionOptions: string[]
    regionFilter: string
    onRegionChange: (region: string) => void
    onReset: () => void
    resetEnabled: boolean
    className?: string
}

export function RegionsFilter({ regionOptions, regionFilter, onRegionChange, onReset, resetEnabled, className }: RegionsFilterProps) {
    const t = useTranslations('pazCenter')
    const handleReset = () => {
        onRegionChange('')
        onReset()
    }
    const legendText = t('filters.region.legend')
    const resetLabel = t('filters.region.reset')
    const resetAria = t('filters.region.resetAria')
    return (
        <StyledRegionsFilter className={className}>
            <StyledRegionsFilterLegend>{legendText}</StyledRegionsFilterLegend>
            <StyledRegionsFilterList role="radiogroup" aria-labelledby="region-filter-legend" aria-describedby="region-filter-hint">
                {regionOptions.map(region => {
                    const selected = region === regionFilter
                    const inputId = `region-${region.replace(/\s+/g, '-')}`
                    return (
                        <RegionButton
                            key={region}
                            $selected={selected}
                            htmlFor={inputId}
                        >
                            <input
                                type="radio"
                                id={inputId}
                                name="region-filter"
                                value={region}
                                checked={selected}
                                onChange={() => onRegionChange(selected ? '' : region)}
                                aria-label={region}
                            />
                            {region}
                        </RegionButton>
                    )
                })}
            </StyledRegionsFilterList>
            <StyledRegionsResetBtn
                onClick={handleReset}
                disabled={!resetEnabled}
                aria-label={resetAria}
            >
                {resetLabel}
            </StyledRegionsResetBtn>
        </StyledRegionsFilter>
    )
}
